/**
 * Normalised modeller models.
 *
 * These mirror the API's own field names deliberately. A renamed field here
 * is a translation layer nobody asked for, and it makes switching between the
 * live client and the fixture client a rewrite rather than a swap.
 *
 * Where a model carries something the API does NOT store, it is marked
 * DERIVED and computed in one place. `dateRange` is the example: the design
 * shows it, the database has no such column, and inventing one server-side to
 * match a mock would be the wrong direction of travel.
 */

/** What a PARSER resolved about one fact. Not a human decision — see below. */
export type FactStatus = 'deterministic' | 'absent' | 'refuse';

/** What a PERSON decided about a suggestion. `pending` is the absence of a
 *  live confirmation, derived server-side (056), never stored. These two are
 *  independent: an `absent` fact stays `pending` forever, because there is
 *  nothing to confirm. They must never be merged into one status chip. */
export type ConfirmationStatus = 'pending' | 'accepted' | 'edited' | 'rejected';

/**
 * The `production_status` enum, verbatim.
 *
 * An earlier version of this type invented `scheduled` and `active` and
 * omitted `confirmed` and `shooting`. Nothing failed, because the badge map
 * fell through to a default and the API never sends the invented ones — but a
 * status control built on it would have offered values the database refuses.
 */
export type ProductionStatus =
  | 'prospect'    // being quoted; may never happen
  | 'quoted'      // an estimate has gone out
  | 'confirmed'   // won, not yet shooting
  | 'shooting'
  | 'wrapped'
  | 'cancelled'
  | 'lost';       // quoted and not won — the estimate is still evidence

/**
 * The body `POST /v1/productions` accepts, verbatim from `ProductionIn`.
 *
 * REQUIRED: slug (1-64), name, timezone. Everything else is optional, and
 * `status` defaults to 'prospect' server-side. `created_by` is free text — the
 * server has no user records, so it is a name someone types, not an identity.
 */
export interface NewProduction {
  slug: string;
  name: string;
  timezone: string;
  client?: string | null;
  production_company?: string | null;
  status?: ProductionStatus;
  notes?: string | null;
  created_by?: string | null;
}

/** In the enum's own order, which is the lifecycle order. */
export const PRODUCTION_STATUSES: readonly ProductionStatus[] = [
  'prospect', 'quoted', 'confirmed', 'shooting', 'wrapped', 'cancelled', 'lost',
];

export interface ProductionUnit {
  id: number;
  unit_key: string;
  display_name: string;
}

/**
 * A SHOOT DAY IS A UNIT-DAY: one operating unit on one local date. Two units
 * routinely share a date with different calls — that is why 060 exists — so
 * `local_date` alone never identifies a row.
 */
export interface ShootDay {
  id: number;
  local_date: string;
  day_number: number | null;
  day_type: string;
  production_unit_id: number | null;
  unit_display_name: string | null;

  /** PLANNED timing. Predictive facts, not what the DPR recorded. */
  call_time_local: string | null;
  estimated_wrap_local: string | null;
  pre_rig_local: string | null;
  planned_length_h: number | null;
  /** True when the length is call → wrap arithmetic rather than an authored
   *  number. The UI may say so; it must not present it as measured. */
  day_length_is_derived: boolean | null;

  supply_context: string | null;
  lighting_condition: string | null;
}

export interface Production {
  id: number;
  slug: string;
  name: string;
  client: string | null;
  production_company: string | null;
  timezone: string;
  status: ProductionStatus;
  notes: string | null;
  created_at: string;
  shoot_days: ShootDay[];
  units: ProductionUnit[];
  droid_count: number;
}

/**
 * What `GET /v1/productions` ACTUALLY provides.
 *
 * The response includes `shoot_days`, `units` and `droid_count` keys, but they
 * are ALWAYS EMPTY — the list query does not populate them. An empty array
 * there means "not included in this response", which is indistinguishable
 * from "none exist", so a card that derived a date range from them reported
 * "No dates yet · 0 unit-days" for a production with four.
 *
 * They are omitted from this type deliberately. A component cannot then read
 * a number the list endpoint never knew.
 */
export type ProductionSummary =
  Omit<Production, 'shoot_days' | 'units' | 'droid_count'>;

export interface EquipmentClass {
  equipment_class_id: number;
  key: string;
  name: string;
  category: string;
  min_kw: number;
  typical_kw: number;
  max_kw: number;
  peak_kw: number;
  duty_cycle: number;
  typical_duration_h: number;
  kind: string;
  confidence: string;
  source_key: string | null;
  source_version: string | null;
  power_factor: number | null;
  connected_kw: number | null;
  power_source: string | null;
}

/** One parsed fact of one shoot-day suggestion. */
export interface ExtractedFact {
  fact_id: number | null;
  status: FactStatus | null;
  value: string | null;
  raw_value_text: string | null;
  resolution_note: string | null;
  /** What the layout rule admitted, in the order it found them. A candidate
   *  is NOT a suggestion and is never offered for confirmation. */
  candidates: string[];
  confirmation_status: ConfirmationStatus;
  confirmation_id: number | null;
}

export interface DaySuggestion {
  id: number;
  day_number: number | null;
  local_date: string | null;
  raw_unit_label: string | null;
  day_identifier_raw: string | null;
  evidence_quote: string | null;
  page_no: number | null;
  segment_count: number;
  crew_call: ExtractedFact;
  estimated_wrap: ExtractedFact;
}

export interface EstimateBand {
  low: number | null;
  expected: number;
  high: number | null;
}

/**
 * THE API'S OWN SHAPE. The three bounds arrive as nine FLAT fields —
 * `daily_kwh_low`, `daily_kwh_expected`, `daily_kwh_high` and so on — not as
 * nested objects. An earlier version of this interface invented the nested
 * form; the fixtures matched the invention, so fixture mode rendered and live
 * mode crashed on `undefined.low`. Mirror the API and derive once.
 */
export interface Estimate {
  id: number;
  shoot_day_id: number | null;
  status: string;
  profile_key: string | null;
  profile_version: number | null;
  method_version: string;
  daily_kwh_low: number;
  daily_kwh_expected: number;
  daily_kwh_high: number;
  /* NULLABLE, and that is the server being honest: when no inrush data
     exists the bounds cannot be computed, so it returns null rather than
     repeating `expected` three times. `peak_band_resolved` says which state
     this is. */
  peak_kw_low: number | null;
  peak_kw_expected: number;
  peak_kw_high: number | null;
  sustained_kw_low: number;
  sustained_kw_expected: number;
  sustained_kw_high: number;
  /** The profile, returned with the estimate — NOT a separate request. */
  hourly: EstimateHour[];
  /** False when no inrush data exists, so the three bounds are identical and
   *  the engine emits ONE collapsed figure rather than a band nobody
   *  computed. The UI must show the collapse as a consequence of missing
   *  data, not as a design choice. */
  peak_band_resolved: boolean;
  coincidence_factor: number | null;
  item_count: number;
  assumed_item_count: number;
  call_time_local: string | null;
  estimated_wrap_local: string | null;
  day_length_h: number | null;
}

/**
 * One bucket of the load profile, exactly as `HourOut` returns it.
 *
 * `hour_index` is SIGNED elapsed hours from local midnight of the estimate's
 * origin — not an offset from the call, and NOT wrapped. -1 is 23:00 the day
 * before; 30 is 06:00 the next, so an overnight shoot stays continuous.
 *
 * `local_hour` is NULL on a relative estimate and that is load-bearing: with
 * no clock anchor, hour 0 is an arbitrary model origin, and rendering it as
 * midnight would assert a start nobody supplied. The axis must fall back to
 * elapsed hours rather than invent a clock.
 *
 * THREE kW VALUES, because the table stores three. Exposing only `expected`
 * would foreclose the band the data already has.
 */
export interface EstimateHour {
  hour_index: number;
  local_hour: number | null;
  day_offset: number;
  kw_low: number;
  kw_expected: number;
  kw_high: number;
}

/** Registered documentary identity, plus one derived count. */
export interface DocumentSummary {
  id: number;
  kind: string;
  filename: string;
  media_type: string;
  byte_size: number;
  sha256: string;
  storage_uri: string;
  page_count: number | null;
  uploaded_at: string;
  uploaded_by: string | null;
  note: string | null;
  /** ATTEMPTS, not the latest. 0 = registered but never read. */
  extraction_count: number;
}

/** One extraction attempt. Re-parsing is historical, so these are a list. */
export interface ExtractionAttempt {
  id: number;
  status: string;
  method: string;
  parser_version: string | null;
  requested_at: string;
  completed_at: string | null;
  requested_by: string | null;
  error: string | null;
  day_count: number;
  fact_count: number;
}

// ------------------------------------------------------------------ derived

/**
 * DERIVED, never stored. The design shows a date range on a production card;
 * the database has no such column and should not grow one to match a mock.
 * Computed from the unit-days that already exist.
 */
export function dateRange(days: Pick<ShootDay, 'local_date'>[]): string | null {
  if (!days.length) return null;
  const dates = [...days.map((d) => d.local_date)].sort();
  const MONTHS = ['Jan', 'Feb', 'Mar', 'Apr', 'May', 'Jun',
                  'Jul', 'Aug', 'Sep', 'Oct', 'Nov', 'Dec'];
  const fmt = (iso: string) => {
    const parts = iso.split('-');
    const month = MONTHS[Number(parts[1]) - 1] ?? parts[1];
    return `${month} ${Number(parts[2])}`;
  };
  const first = fmt(dates[0] as string);
  const last = fmt(dates[dates.length - 1] as string);
  return first === last ? first : `${first} – ${last}`;
}

/** How many DISTINCT DATES, as against how many unit-days. Two units on one
 *  date is one shooting date and two rows, and conflating them miscounts the
 *  schedule. */
export function distinctDates(days: Pick<ShootDay, 'local_date'>[]): number {
  return new Set(days.map((d) => d.local_date)).size;
}

/** DERIVED from the date, not a stored column. */
export function dayTiming(localDate: string, today = new Date()): 'past' | 'today' | 'future' {
  const t = today.toISOString().slice(0, 10);
  return localDate === t ? 'today' : localDate < t ? 'past' : 'future';
}

/**
 * THE ONE PLACE a flat triple becomes a band.
 *
 * Derived, not an API field. Every chart and figure goes through this, so no
 * component can invent its own reading of which of the nine numbers belong
 * together.
 */
export function band(e: Estimate, metric: 'daily_kwh' | 'peak_kw' | 'sustained_kw'): EstimateBand {
  return {
    low: e[`${metric}_low`] ?? null,
    expected: e[`${metric}_expected`],
    high: e[`${metric}_high`] ?? null,
  };
}

/** A band is only a band when both bounds exist AND they differ from the
 *  middle. Null bounds are not zero and are not equal — they are absent. */
export function hasSpread(b: EstimateBand): boolean {
  return b.low != null && b.high != null
    && !(b.low === b.expected && b.expected === b.high);
}

/**
 * A unit-day's unit, named — and never invented.
 *
 * The legacy `unspecified` unit means NOBODY WAS ASKED which unit it was; 060
 * created it deliberately and its comment says so. Relabelling it "Main Unit"
 * would assert exactly the thing that was not recorded, so it reads as the
 * absence it is.
 *
 * ONE implementation, used by every screen that shows a unit. Two would drift
 * and put two different words for the same row on two pages.
 */
export function unitLabel(day: Pick<ShootDay, 'unit_display_name'>): string {
  const n = day.unit_display_name;
  if (!n || n.toLowerCase().includes('unspecified')) return 'Unit not specified';
  return n;
}

// ------------------------------------------------------- write bodies (v2)
//
// Each of these is a transcription of a real pydantic model. The rule that
// produced the last round of bugs applies here too: a field this file names
// that the API does not accept is an invention, and a field the API accepts
// but SILENTLY DISCARDS is worse — the form would say it recorded something
// it did not.

/**
 * The body `POST /v1/productions/{slug}/shoot-days` accepts.
 *
 * A SUBSET of `ShootDayIn`, and the omissions are deliberate:
 *
 * - `supply_context` and `lighting_condition` ARE declared on `ShootDayIn`
 *   and are NEVER INSERTED by `add_shoot_day` — the INSERT does not list the
 *   columns. Sending them is accepted with a 201 and the value vanishes, so
 *   they are not offered. Verified by round-trip, not by reading.
 * - `planned_length_h` is refused alongside `estimated_wrap_local` (059) and
 *   is derived from it when a wrap is given, so the form takes the wrap.
 * - `location_id` needs a location picker that does not exist.
 *
 * `unit_key` omitted resolves to 'unspecified' server-side — deliberately not
 * 'main'. `unit_display_name` is used only when that key is first seen.
 */
export interface NewShootDay {
  local_date: string;
  unit_key?: string | null;
  unit_display_name?: string | null;
  day_number?: number | null;
  call_time_local?: string | null;
  estimated_wrap_local?: string | null;
  day_type?: string | null;
  notes?: string | null;
}

/**
 * The body `POST /v1/productions/{slug}/documents` accepts, from `DocumentIn`.
 *
 * All six required fields are obtainable in the browser: five from the `File`
 * object, and `sha256` from `crypto.subtle.digest` over its bytes. The
 * exception is `storage_uri`, which is neither derivable nor server-knowable
 * — it says where the canonical file LIVES, and only a person knows that.
 */
export interface NewDocument {
  kind: DocumentKind;
  filename: string;
  media_type: string;
  byte_size: number;
  sha256: string;
  storage_uri: string;
  page_count?: number | null;
  uploaded_by?: string | null;
  note?: string | null;
}

/**
 * What REGISTRATION returns: `DocumentOut`, which is `DocumentListItem`
 * MINUS `extraction_count`. The count is derived by the list query and is
 * simply not in this response — typing it as the list shape would let a
 * component read `extraction_count` and get `undefined` where it expects 0.
 */
export type RegisteredDocument = Omit<DocumentSummary, 'extraction_count'>;

export type DocumentKind =
  'schedule' | 'script' | 'call_sheet' | 'equipment_list' | 'other';

export const DOCUMENT_KINDS: readonly DocumentKind[] = [
  'call_sheet', 'schedule', 'script', 'equipment_list', 'other',
];

/**
 * The `extraction` an INGEST returns.
 *
 * A different shape from `ExtractionAttempt`, which is what the history LIST
 * returns: `ExtractionOut` carries `segment_count` and `notes` and has no
 * `fact_count` or `error`, and `ExtractionListItem` is the reverse. Two
 * pydantic models, so two types — reusing one would put a field on screen
 * that the response never contains.
 */
export interface IngestedExtraction {
  id: number;
  status: string;
  method: string;
  parser_version: string | null;
  requested_at: string;
  completed_at: string | null;
  requested_by: string | null;
  day_count: number;
  segment_count: number;
  /** What the parser saw and could not place. Reported, never dropped. */
  notes: string[];
}

/** What one ingest returns. Not just an id: the caller learns from the upload
 *  itself that a fact was refused, rather than from a second request it might
 *  not think to make. */
export interface CallSheetExtraction {
  extraction: IngestedExtraction;
  parser_name: string;
  source_sha256: string;
  shoot_day_suggestion_id: number;
  local_date: string | null;
  raw_unit_label: string | null;
  day_identifier_raw: string | null;
  day_number: number | null;
  crew_call: ParsedFact;
  estimated_wrap: ParsedFact;
}

/**
 * A fact as the INGEST reports it — parser resolution only.
 *
 * Deliberately narrower than `ExtractedFact`: `FactOut` carries no
 * `confirmation_status` and no `confirmation_id`, because nothing has been
 * confirmed at the moment a document is read. Widening this to the review
 * screen's type would put a decision state on screen that the response does
 * not contain and that is, at this instant, always 'pending' by definition.
 */
export interface ParsedFact {
  fact_id: number;
  status: FactStatus;
  value: string | null;
  raw_value_text: string | null;
  resolution_note: string | null;
  candidates: string[];
}

/** The body every confirmation route accepts. `shoot_day_id` is REQUIRED and
 *  is never resolved from a date — two units routinely share one. */
export interface ConfirmFact {
  shoot_day_id: number;
  decision: 'accepted' | 'edited' | 'rejected';
  confirmed_by: string;
  /** HH:MM. Omitted takes the parser's value. */
  accepted_value?: string | null;
  reject_reason?: string | null;
}

/** A historical event. Never edited; superseded. */
export interface Confirmation {
  id: number;
  target_table: string;
  target_id: number;
  target_field: string;
  suggested_value: string;
  accepted_value: string | null;
  source_document_sha256: string;
  source_document_filename: string;
  decision: string;
  confirmed_by: string;
}
